import {
  Add,
  Delete,
  Edit,
  OpenInNew,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LucideIcon from "../../components/LucideIcon";
import { hostname, User } from "../../utils";
import AppFormDialog, { WhitelabelApp } from "./dialogs/AppFormDialog";

const FEATURES_DOC_HINT =
  "Enabled apps appear in every user's side menu for this whitelabel.";

export default function WhitelabelApps({
  whitelabelId,
  currentUser,
  getToken,
}: {
  whitelabelId: string;
  currentUser: User;
  getToken: () => Promise<string>;
}) {
  const [apps, setApps] = useState<WhitelabelApp[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<WhitelabelApp | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  useEffect(() => {
    loadApps();
  }, [whitelabelId]);

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const loadApps = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApps(Array.isArray(response.data.apps) ? response.data.apps : []);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load apps");
    } finally {
      setLoading(false);
    }
  };

  const persistApps = async (nextApps: WhitelabelApp[]) => {
    setSaving(true);
    try {
      const token = await getToken();
      const response = await axios.put(
        `${hostname}/whitelabel/${whitelabelId}`,
        { apps: nextApps },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApps(Array.isArray(response.data.apps) ? response.data.apps : []);
      return true;
    } catch (err: any) {
      showSnackbar(
        err.response?.data?.error || "Failed to save apps",
        "error"
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (app: WhitelabelApp) => {
    const exists = apps.some((a) => a.id === app.id);
    const nextApps = exists
      ? apps.map((a) => (a.id === app.id ? app : a))
      : [...apps, app];

    const ok = await persistApps(nextApps);
    if (ok) {
      showSnackbar(exists ? "App updated" : "App added", "success");
      setEditorOpen(false);
      setEditingApp(null);
    }
  };

  const handleDelete = async (app: WhitelabelApp) => {
    if (
      !window.confirm(
        `Delete the app "${app.name}"? This will remove it from the user menu.`
      )
    ) {
      return;
    }
    const nextApps = apps.filter((a) => a.id !== app.id);
    const ok = await persistApps(nextApps);
    if (ok) {
      showSnackbar("App deleted", "success");
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        {FEATURES_DOC_HINT} Add an external URL (rendered as an iframe) or pick
        a Public app you've built in the platform. Disable an app to hide it
        without deleting it.
      </Alert>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">Custom Apps</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingApp(null);
            setEditorOpen(true);
          }}
          disabled={saving}
        >
          Add App
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 60 }}>Icon</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>URL</TableCell>
                <TableCell sx={{ width: 120 }}>Enabled</TableCell>
                <TableCell sx={{ width: 140 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apps.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 4, textAlign: "center" }}>
                      <Typography color="text.secondary">
                        No apps configured yet. Click "Add App" to embed an
                        external website in the user menu.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {apps.map((app) => (
                <TableRow key={app.id} hover>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        backgroundColor: "action.hover",
                      }}
                    >
                      <LucideIcon name={app.icon} size={20} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={500}>{app.name}</Typography>
                  </TableCell>
                  <TableCell>
                    {app.url ? (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 360,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={app.url}
                        >
                          {app.url}
                        </Typography>
                        <Tooltip title="Open in new tab">
                          <IconButton
                            size="small"
                            component="a"
                            href={app.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : app.appId ? (
                      <Chip
                        label="Platform app"
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {app.enabled ? (
                      <Chip label="Enabled" color="primary" size="small" />
                    ) : (
                      <Chip label="Off" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={() => {
                          setEditingApp(app);
                          setEditorOpen(true);
                        }}
                        disabled={saving}
                        size="small"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => handleDelete(app)}
                        disabled={saving}
                        size="small"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <AppFormDialog
        open={editorOpen}
        app={editingApp}
        onClose={() => {
          setEditorOpen(false);
          setEditingApp(null);
        }}
        onSubmit={handleSubmit}
        getToken={getToken}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
